import React from "react";
import { Button, Progress } from "antd";
import {
  ExportOutlined,
  PoweroffOutlined,
  RightOutlined,
} from "@ant-design/icons";
import { SUBSCRIPTION_CONFIG } from "../../config/zinihubConfig";
import { SUBSCRIPTION_PROFILE_LABELS } from "../../labels/subscriptionProfileLabels";
import {
  buildProfileSubscriptionSummary,
  formatSubscriptionDateShort,
} from "../../utils/subscriptionDisplay";
import styles from "../../styles/AdminAppShell.module.css";

function getStatusDotClass(expiryState) {
  if (expiryState === "expired") return styles.profileStatusDotExpired;
  if (expiryState === "tomorrow") return styles.profileStatusDotCritical;
  if (expiryState === "renewSoon") return styles.profileStatusDotWarning;
  return styles.profileStatusDotHealthy;
}

function getProgressColor(expiryState) {
  if (expiryState === "expired") return "var(--zh-text-muted)";
  if (expiryState === "tomorrow") return "var(--zh-error, #ef4444)";
  if (expiryState === "renewSoon") return "var(--zh-warning, #f59e0b)";
  return "var(--zh-success, #22c55e)";
}

function resolvePrimaryExpiryLabel(summary) {
  const { remainingDays, expiryState } = summary;
  if (expiryState === "expired") {
    return SUBSCRIPTION_PROFILE_LABELS.alertExpiredTitle;
  }
  if (expiryState === "tomorrow") {
    return SUBSCRIPTION_PROFILE_LABELS.alertTomorrowTitle;
  }
  return SUBSCRIPTION_PROFILE_LABELS.expiresInDays(remainingDays);
}

function resolveAlertCopy(summary) {
  const { expiryState } = summary;
  if (expiryState === "expired") {
    return {
      title: SUBSCRIPTION_PROFILE_LABELS.alertExpiredTitle,
      body: SUBSCRIPTION_PROFILE_LABELS.alertExpiredBody,
      cta: SUBSCRIPTION_PROFILE_LABELS.renewSubscription,
    };
  }
  if (expiryState === "tomorrow") {
    return {
      title: SUBSCRIPTION_PROFILE_LABELS.alertTomorrowTitle,
      body: SUBSCRIPTION_PROFILE_LABELS.alertTomorrowBody,
      cta: SUBSCRIPTION_PROFILE_LABELS.renewNow,
    };
  }
  if (expiryState === "renewSoon") {
    return {
      title: SUBSCRIPTION_PROFILE_LABELS.alertRenewSoonTitle,
      body: SUBSCRIPTION_PROFILE_LABELS.alertRenewSoonBodyActive,
      cta: SUBSCRIPTION_PROFILE_LABELS.renewSubscription,
    };
  }
  return null;
}

function ProfileSubscriptionSection({ summary, onManageSubscription }) {
  if (!summary.hasSubscription) return null;

  const alertCopy = resolveAlertCopy(summary);
  const showRenewCta = Boolean(alertCopy);
  const progressColor = getProgressColor(summary.expiryState);

  return (
    <div className={styles.profileSubscriptionSection}>
      {alertCopy && (
        <div className={`${styles.profileAlertBanner} ${styles[`profileAlertBanner_${summary.expiryState}`]}`}>
          <strong>{alertCopy.title}</strong>
          <span>{alertCopy.body}</span>
        </div>
      )}

      <div className={styles.profilePlanHeader}>
        <span className={`${styles.profileStatusDot} ${getStatusDotClass(summary.expiryState)}`} aria-hidden="true" />
        <div className={styles.profilePlanHeaderMain}>
          <div className={styles.profilePlanTitleRow}>
            <strong className={styles.profilePlanTitle}>{summary.planTitle}</strong>
            {summary.trialActive && (
              <span className={styles.profileTrialPill}>
                {summary.trialDays
                  ? SUBSCRIPTION_PROFILE_LABELS.trialBadgeDays(summary.trialDays)
                  : SUBSCRIPTION_PROFILE_LABELS.trialBadge}
              </span>
            )}
          </div>
          <div className={styles.profileExpiryPrimary}>
            {resolvePrimaryExpiryLabel(summary)}
          </div>
          {summary.expiryState !== "expired" && (
            <div className={styles.profileExpirySecondary}>
              {formatSubscriptionDateShort(summary.expiryDate)}
            </div>
          )}
        </div>
      </div>

      {summary.totalDays > 0 && summary.expiryState !== "expired" && (
        <div className={styles.profileProgressBlock}>
          <Progress
            percent={summary.progressPercent}
            strokeColor={progressColor}
            showInfo={false}
            size="small"
            className={styles.profileProgressBar}
          />
          <div className={styles.profileProgressMeta}>
            <span>{SUBSCRIPTION_PROFILE_LABELS.daysRemaining(summary.remainingDays)}</span>
            {summary.startDate && (
              <span className={styles.profileProgressRatio}>
                {SUBSCRIPTION_PROFILE_LABELS.progressDays(
                  summary.remainingDays,
                  summary.totalDays
                )}
              </span>
            )}
          </div>
        </div>
      )}

      <Button
        type={showRenewCta ? "primary" : "default"}
        block
        className={[
          styles.profileSubscriptionCta,
          !showRenewCta ? styles.profileSubscriptionCtaSecondary : "",
        ].filter(Boolean).join(" ")}
        onClick={onManageSubscription}
      >
        {showRenewCta
          ? alertCopy.cta
          : SUBSCRIPTION_PROFILE_LABELS.manageSubscription}
        {!showRenewCta && <RightOutlined className={styles.profileCtaIcon} />}
      </Button>
    </div>
  );
}

export default function ProfileDropdown({
  decoded,
  subscriptionCatalog,
  hubProfile,
  onLogout,
  onManageSubscription,
}) {
  const summary = buildProfileSubscriptionSummary(subscriptionCatalog, hubProfile);
  const supportUrl = SUBSCRIPTION_CONFIG.LABELS.PRICING.supportUrl;

  if (!decoded) {
    return (
      <div className={styles.profileDropdown}>
        <div
          className={styles.dropdownLogout}
          role="button"
          tabIndex={0}
          onClick={onLogout}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") onLogout();
          }}
        >
          <PoweroffOutlined /> {SUBSCRIPTION_PROFILE_LABELS.signOut}
        </div>
      </div>
    );
  }

  const displayName = `${decoded.UserProfile?.FirstName || ""} ${decoded.UserProfile?.LastName || ""}`.trim();
  const avatarInitial = displayName.slice(0, 1).toUpperCase() || "U";

  return (
    <div className={styles.profileDropdown}>
      <div className={styles.dropdownHeroCompact}>
        <div className={styles.dropdownAvatar}>{avatarInitial}</div>
        <div>
          <div className={styles.dropdownName}>{displayName || "User"}</div>
          <div className={styles.dropdownRole}>{SUBSCRIPTION_PROFILE_LABELS.accountRole}</div>
        </div>
      </div>

      <ProfileSubscriptionSection
        summary={summary}
        onManageSubscription={onManageSubscription}
      />

      <a
        href={supportUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={styles.profileHelpLink}
      >
        {SUBSCRIPTION_PROFILE_LABELS.helpSupport}
        <ExportOutlined className={styles.profileHelpLinkIcon} aria-hidden="true" />
      </a>

      <div
        className={styles.dropdownLogout}
        role="button"
        tabIndex={0}
        onClick={onLogout}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") onLogout();
        }}
      >
        <PoweroffOutlined /> {SUBSCRIPTION_PROFILE_LABELS.signOut}
      </div>
    </div>
  );
}

export { buildProfileSubscriptionSummary };
